import React from 'react'
import Image from 'next/image'
import { cn } from '@/lib/utils'

/**
 * Intrinsic aspect of the horizontal lockup (master is 3437 × 556, shipped at
 * 1600 × 259). Heights are derived from it so the mark never distorts.
 */
const LOCKUP_ASPECT = 6.18

interface ReliabilityLogoProps {
  className?: string
  /** Rendered width in CSS pixels; height follows the lockup aspect. */
  width?: number
  priority?: boolean
}

/**
 * The full-colour RELIability AI horizontal lockup — the single brand mark for
 * this Command Center. It replaced the former Supervity + RELI Group dual
 * lockup, so it should never be paired with another logo.
 *
 * The wordmark is deep navy (#084064) on transparent, so it needs a light
 * surface. On navy or gradient backgrounds, set it inside a white "safe area"
 * pill (see the PDF briefing header) rather than reaching for a tinted variant.
 */
export function ReliabilityLogo({
  className,
  width = 220,
  priority = true,
}: ReliabilityLogoProps) {
  return (
    <Image
      src='/logos/reliability-ai.png'
      alt='RELIability AI'
      width={width}
      height={Math.round(width / LOCKUP_ASPECT)}
      className={cn('shrink-0', className)}
      priority={priority}
    />
  )
}

interface ReliabilityMarkProps {
  className?: string
  size?: number
}

/**
 * The square "AI" tile lifted from the right of the lockup — for the collapsed
 * sidebar, the favicon and anywhere the full lockup cannot breathe. It carries
 * its own teal (#136E8A) field, so it sits happily on light or dark surfaces.
 */
export function ReliabilityMark({
  className,
  size = 36,
}: ReliabilityMarkProps) {
  return (
    <Image
      src='/logos/reliability-ai-mark.png'
      alt='RELIability AI'
      width={size}
      height={size}
      className={cn('shrink-0 rounded-xl', className)}
      priority
    />
  )
}
